/**
 * Formatting helpers shared by the persona view's panels (§4.5.3).
 *
 * Two things live here because three files need them and none owns them:
 *   - the engine id translation (conversation engines are `chatgpt`, the EngineLogo
 *     registry calls the same engine `openai` — `engineKey('chatgpt')` is `unknown`)
 *   - `pct`, which returns `null` rather than a dash: the mock README forbids `—`
 *     placeholders, so the caller must render its own « en cours de mesure ».
 */

import { ENGINE_META, engineKey } from '../brand/EngineLogo';
import type { ConversationCounters } from '@traaker/shared';

/** Conversation engine id → EngineLogo/ENGINE_META id. */
export function engineLogoId(engine: string): string {
  return engine === 'chatgpt' ? 'openai' : engine;
}

/** Display label; falls back to the raw id rather than to ENGINE_META.unknown's « Moteur ». */
export function engineDisplay(engine: string): string {
  const key = engineKey(engineLogoId(engine));
  return key === 'unknown' ? engine : ENGINE_META[key].label;
}

/**
 * URL of the engine's logo, for contexts that cannot mount `<EngineLogo>` — an SVG
 * chart draws `<image href>`, not a React tree. `undefined` for an unknown engine:
 * the caller renders the label alone rather than a broken image.
 */
export function engineIconSrc(engine: string): string | undefined {
  const key = engineKey(engineLogoId(engine));
  const file: Partial<Record<string, string>> = {
    openai: 'chatgpt',
    perplexity: 'perplexity',
    gemini: 'gemini',
    claude: 'claude',
    mistral: 'mistral',
    // Google AI Overview and AI Overview are one mark.
    ai_overview: 'ai_overview',
    google: 'ai_overview',
  };
  const f = file[key];
  return f ? `/engines/${f}.svg` : undefined;
}

/** `null` in ⇒ `null` out. NEVER a dash (mock README: an empty state says what is empty). */
export function pct(v: number | null | undefined): string | null {
  return v == null ? null : `${Math.round(v * 100)} %`;
}

/** Σ t·cDrop[t] / Σ cDrop[t] — the mean turn a run stops being cited at. `null` when nothing dropped. */
export function meanDropOutTurn(stats: ConversationCounters | undefined): number | null {
  const drops = stats?.cDrop ?? [];
  let num = 0;
  let den = 0;
  for (let t = 0; t < drops.length; t++) {
    num += t * (drops[t] ?? 0);
    den += drops[t] ?? 0;
  }
  return den > 0 ? num / den : null;
}

/** « 22 sept. » — a chart axis label. */
export function formatDayShort(day: string, locale: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}
